/**
 * Per-sheet column schema shared by both ArTable XLSX writers,
 * `exportArTableToExcel` and `createXlsxWorkbookBlob`, whose forked assembly would
 * otherwise drift on time-range splitting, typing and width (SYD-2279). `columns`
 * arrive visibility-filtered. `type` is presentation — column `numFmt`, `(CT)`
 * suffix, width — never a gate on what a cell writes (`coerceArTableSheetCell`), so
 * sampling skips bundle-header and suppressed rows, and `isTimeRangeColumn` reads
 * raw cells. Samples take leading eligible rows only — 5 split, 10 type, 100 width.
 *
 * Every cell reads through the clipboard's projection: `applyArFormulaResultsToCells`
 * over `computeArRowFormulas`, memoized per `rowId` for the life of one call, then
 * `cells[columnId].value`. That puts a computed result exactly where a stored one
 * sits — plain targets in `value`, dotted targets inside the root column's
 * composite — so a server-paginated export, whose rows never passed through the
 * grid, exports the same numbers as the grid, and no surface reads `calcInput`. A
 * row the engine declines to compute (`suppressFormulas`, aggregations, bundle
 * headers) passes through untouched, keeping its writer-set `value`; a `null`
 * result exports blank rather than a stale number, and a table owning no formula
 * target skips the projection entirely. A column-less table yields an empty
 * schema; only `EXPORT_MAX_ROWS` throws, so one table cannot abort a workbook.
 */

import {
  EXPORT_COLUMN_MIN_WIDTH,
  EXPORT_COLUMN_MAX_WIDTH,
  EXPORT_MAX_ROWS,
} from '#types/System.Parameters.types';
import type {
  ArCell,
  ArCellValue,
  ArColumnDefinition,
  ArRowData,
  ArRowId,
  TypeId,
} from '#types/System.Artifacts.types';
import { UIElementType } from '#types/System.types';
import type { FieldType } from '#types/System.types';
import {
  applyArFormulaResultsToCells,
  computeArRowFormulas,
  resolveArFormulaOwnedTypeIds,
} from '#types';
import type { ArFormulaEngineConfig } from '#types';
import type { DocEntry } from '@/store/Store.types';
import {
  convertTimestampToDate,
  isRangeValue,
  getDisplayLength as legacyGetDisplayLength,
} from '@/components/exportTable/exportTableToExcel';
import type { FormattedCellValue } from '@/components/CompositeField/formattedCellValue';
import {
  formatShippingOrCompositeCell,
  type ShippingOrCompositeCellValue,
} from './formatShippingOrCompositeCell';
import type { BundleHeaderCellReader } from './createBundleHeaderCellReader';

type ArTableSheetRow = { rowId: ArRowId; entry: DocEntry<ArRowData> };
type ArTableSheetValue = ShippingOrCompositeCellValue;

type ArTableSheetSchemaItem = {
  column: string;
  type: typeof String | typeof Number | typeof Boolean | typeof Date;
  value: (row: ArTableSheetRow) => ArTableSheetValue;
  width: number;
  wrap?: boolean;
  format?: string;
};

type ArTableSheetSchema = {
  rows: ArTableSheetRow[];
  schema: ArTableSheetSchemaItem[];
};

type BuildArTableSheetSchemaParams = {
  columns: readonly ArColumnDefinition[];
  visibleRowIds: readonly ArRowId[];
  rowsMap: ReadonlyMap<string, DocEntry<ArRowData>>;
  getColumnLabel: (column: ArColumnDefinition) => string;
  /**
   * True when the cell sits on a bundle member row whose column withholds it from
   * the active view. Suppressed cells yield no value and shape no column.
   */
  isCellSuppressed: (rowId: ArRowId, columnId: string) => boolean;
  /**
   * Resolves a bundle-header row's cell, or reports the row is no header at all.
   * A header cell wins over every rule below it — suppression and the overlaid
   * cell read included — and is then formatted like any other value.
   */
  getBundleHeaderCell: BundleHeaderCellReader;
  /** Names the formula columns and drives their evaluation; no caller supplies values. */
  formulaConfig: ArFormulaEngineConfig;
  fieldTypes?: Record<string, FieldType>;
  formatCompositeValue?: (columnTypeId: string, cellValue: unknown) => string | number | FormattedCellValue | undefined;
};

const TIME_RANGE_SAMPLE_ROWS = 5;
const COLUMN_TYPE_SAMPLE_ROWS = 10;
const COLUMN_WIDTH_SAMPLE_ROWS = 100;

const DATE_NUMBER_FORMAT = 'MM/DD/YYYY HH:mm:ss';

/**
 * Calculates optimal column width from the header and the sampled content.
 */
const calculateColumnWidth = (
  headerLabel: string,
  sampleRows: readonly ArTableSheetRow[],
  getValue: (row: ArTableSheetRow) => ArTableSheetValue,
): number => {
  const lengths = sampleRows
    .slice(0, COLUMN_WIDTH_SAMPLE_ROWS)
    .map((row) => legacyGetDisplayLength(getValue(row)));
  const maxLength = Math.max(headerLabel.length, ...lengths);

  return Math.min(EXPORT_COLUMN_MAX_WIDTH, Math.max(EXPORT_COLUMN_MIN_WIDTH, maxLength + 2));
};

/**
 * Detects the Excel type for a column from the first typed value in the sample;
 * strings do not settle the type, so a later Date, Boolean or Number wins.
 */
const detectColumnType = (
  sampleRows: readonly ArTableSheetRow[],
  getValue: (row: ArTableSheetRow) => ArTableSheetValue,
): typeof String | typeof Number | typeof Boolean | typeof Date => {
  const typed = sampleRows
    .slice(0, COLUMN_TYPE_SAMPLE_ROWS)
    .map(getValue)
    .find((value) => value instanceof Date || typeof value === 'boolean' || typeof value === 'number');

  if (typed instanceof Date) return Date;
  if (typeof typed === 'boolean') return Boolean;
  if (typeof typed === 'number') return Number;

  return String;
};

/**
 * Checks if a column contains TimeRange values that should be split.
 */
const isTimeRangeColumn = (sampleRows: readonly ArTableSheetRow[], columnId: string): boolean =>
  sampleRows
    .slice(0, TIME_RANGE_SAMPLE_ROWS)
    .some((row) => isRangeValue((row.entry.data?.cells?.[columnId] as ArCell | undefined)?.value));

function buildArTableSheetSchema(params: BuildArTableSheetSchemaParams): ArTableSheetSchema {
  const {
    columns,
    visibleRowIds,
    rowsMap,
    getColumnLabel,
    isCellSuppressed,
    getBundleHeaderCell,
    formulaConfig,
    fieldTypes,
    formatCompositeValue,
  } = params;

  if (visibleRowIds.length > EXPORT_MAX_ROWS) {
    throw new Error(`Cannot export more than ${EXPORT_MAX_ROWS.toLocaleString()} rows`);
  }

  const rows: ArTableSheetRow[] = visibleRowIds
    .map((rowId) => {
      const entry = rowsMap.get(rowId);
      return entry ? { rowId, entry } : null;
    })
    .filter((row): row is ArTableSheetRow => row !== null);

  /** Includes the root of every dotted target, so `sp.basePrice` marks column `sp`. */
  const formulaOwnedTypeIds = resolveArFormulaOwnedTypeIds(formulaConfig.formulaSequence);

  /** One evaluation per row for the whole sheet, not one per formula column. */
  const overlaidCellsByRowId = new Map<ArRowId, Record<TypeId, ArCell>>();
  const readOverlaidCells = (row: ArTableSheetRow): Record<TypeId, ArCell> => {
    /** A formula-less table has nothing to overlay, so it reads its stored cells directly. */
    if (formulaOwnedTypeIds.size === 0) return row.entry.data?.cells ?? {};
    const memoized = overlaidCellsByRowId.get(row.rowId);
    if (memoized) return memoized;
    const cells = applyArFormulaResultsToCells(
      row.entry.data?.cells,
      computeArRowFormulas(row.rowId, row.entry.data, formulaConfig),
    );
    overlaidCellsByRowId.set(row.rowId, cells);
    return cells;
  };

  const schema = columns.flatMap<ArTableSheetSchemaItem>((col) => {
    const columnId = col.typeId;
    const baseLabel = getColumnLabel(col);
    const isFormula = col.uiElementType === UIElementType.Field && formulaOwnedTypeIds.has(columnId);

    /** The only rows allowed to shape this column: the ones its generic read writes. */
    const sampleRows = rows.filter(
      (row) => !getBundleHeaderCell(row.rowId, columnId).isBundleHeader
        && !isCellSuppressed(row.rowId, columnId),
    );

    const getCellValue = (row: ArTableSheetRow): ArCellValue | undefined => {
      const headerCell = getBundleHeaderCell(row.rowId, columnId);
      if (headerCell.isBundleHeader) return headerCell.value;
      if (isCellSuppressed(row.rowId, columnId)) return undefined;
      return readOverlaidCells(row)[columnId]?.value as ArCellValue | undefined;
    };

    if (isTimeRangeColumn(sampleRows, columnId) && !isFormula) {
      /**
       * `convertTimestampToDate` returns an unrecognised shape unchanged, so the edge
       * is narrowed to a real Date here: anything else is blank, never an object the
       * writer would JSON-stringify into the cell.
       */
      const boundary = (edge: 'start' | 'end') => (row: ArTableSheetRow): ArTableSheetValue => {
        const val = getCellValue(row);
        if (!isRangeValue(val)) return null;
        const converted = convertTimestampToDate((val as Record<string, unknown>)[edge]);
        return converted instanceof Date ? converted : null;
      };

      return (['start', 'end'] as const).map((edge) => {
        const label = `${edge === 'start' ? 'Start' : 'End'} ${baseLabel} (CT)`;
        return {
          column: label,
          type: Date,
          value: boundary(edge),
          width: Math.max(EXPORT_COLUMN_MIN_WIDTH, label.length + 2),
          format: DATE_NUMBER_FORMAT,
          wrap: true,
        };
      });
    }

    const formatValue = (row: ArTableSheetRow): ArTableSheetValue =>
      formatShippingOrCompositeCell({
        column: col,
        value: getCellValue(row),
        fieldTypes,
        formatCompositeValue,
      });

    const cellType = detectColumnType(sampleRows, formatValue);
    const headerLabel = cellType === Date ? `${baseLabel} (CT)` : baseLabel;

    return [{
      column: headerLabel,
      type: cellType,
      value: formatValue,
      width: calculateColumnWidth(headerLabel, sampleRows, formatValue),
      wrap: true,
      ...(cellType === Date ? { format: DATE_NUMBER_FORMAT } : {}),
    }];
  });

  return { rows, schema };
}

export { buildArTableSheetSchema };
export type { ArTableSheetRow, ArTableSheetSchemaItem, ArTableSheetValue };
